export interface RetryConfig {
  maxRetries?: number;
  retryDelay?: number;
}
export type FetchWithRetryOptions = {
  url: string;
  options?: RequestInit;
  retries?: number;
  retryDelay?: number;
};

export type ResultErrorVariant = {
  ok: false;
  status: number;
  error: string;
};

type ResultSuccessVariant<T> = {
  ok: true;
  value: T;
};

export type Result<T> = ResultErrorVariant | ResultSuccessVariant<T>;

const fetchWithRetry = async <T>({
  url,
  options = {},
  retries = 3,
  retryDelay = 1000, // one second
}: FetchWithRetryOptions): Promise<Result<T>> => {
  try {
    const response = await fetch(url, options);

    if (response.status >= 500 && retries > 0) {
      await new Promise(resolve => {
        setTimeout(resolve, retryDelay);
      });
      return fetchWithRetry({
        url,
        options,
        retries: retries - 1,
        retryDelay,
      });
    }

    if (!response.ok) {
      return {
        ok: false,
        status: response.status,
        error: response.statusText,
      };
    }

    const json = await response.json();

    return {
      ok: true,
      value: json,
    };
  } catch (error) {
    if (!retries) {
      return {
        ok: false,
        status: error.status ?? 500,
        error: error.toString(),
      };
    }
    await new Promise(resolve => {
      setTimeout(resolve, retryDelay);
    });
    return fetchWithRetry({
      url,
      options,
      retries: retries - 1,
      retryDelay,
    });
  }
};

export default fetchWithRetry;
